/**
 * Webtech HR Engine™ - Dashboard Manager
 * Un cockpit de pilotage d'équipe orienté ACTION.
 * Vision équipe, pas individuelle. Peu de chiffres, mais les bons.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Users,
  User,
  Star,
  Trophy,
  UserX,
  Brain,
  AlertTriangle,
  Calendar,
  TrendingUp,
  TrendingDown,
  Target,
  MessageSquare,
  FileText,
  Download,
  Eye,
  MoreVertical,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Award,
  Clock,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
  UserCheck,
  Activity,
  Gauge,
  Send,
  ArrowRight,
  RefreshCw,
  Crown,
  Network,
  Loader2
} from 'lucide-react';

// ==================== COMPOSANTS ====================

// Badge de statut collaborateur
const StatusBadge = ({ status }) => {
  const config = {
    'OK': { label: 'OK', class: 'bg-green-100 text-green-700 border-green-200' },
    'À suivre': { label: 'À suivre', class: 'bg-amber-100 text-amber-700 border-amber-200' },
    'Critique': { label: 'Critique', class: 'bg-red-100 text-red-700 border-red-200' }
  };
  const c = config[status] || config['OK'];
  return <Badge className={`${c.class} border text-xs`}>{c.label}</Badge>;
};

// Carte KPI
const KPICard = ({ title, value, icon: Icon, trend, subtitle, onClick, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
    purple: "bg-purple-50 text-purple-600"
  };
  
  return (
    <Card 
      className={`hover:shadow-md transition-all ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          {trend && (
            <Badge variant="outline" className={trend > 0 ? 'text-green-600' : 'text-red-600'}>
              {trend > 0 ? '+' : ''}{trend}%
            </Badge>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{title}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

// Carte Recommandation IA
const AIRecommendationCard = ({ recommendation, onAction }) => {
  const iconMap = {
    'AlertTriangle': AlertTriangle,
    'TrendingDown': TrendingDown,
    'TrendingUp': TrendingUp,
    'Lightbulb': Lightbulb,
    'CheckCircle': CheckCircle2
  };
  const Icon = iconMap[recommendation.icon] || Lightbulb;
  
  const typeColors = {
    'warning': 'border-l-amber-500 bg-amber-50/50',
    'danger': 'border-l-red-500 bg-red-50/50',
    'success': 'border-l-green-500 bg-green-50/50',
    'info': 'border-l-blue-500 bg-blue-50/50'
  };
  
  const iconColors = {
    'warning': 'text-amber-600 bg-amber-100',
    'danger': 'text-red-600 bg-red-100',
    'success': 'text-green-600 bg-green-100',
    'info': 'text-blue-600 bg-blue-100'
  };
  
  return (
    <div className={`p-4 rounded-lg border-l-4 ${typeColors[recommendation.type] || typeColors['info']}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full ${iconColors[recommendation.type]} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-700">{recommendation.message}</p>
          <Button 
            size="sm" 
            variant="link" 
            className="p-0 h-auto mt-1 text-xs"
            onClick={() => onAction(recommendation)}
          >
            {recommendation.actionLabel}
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Ligne membre équipe (pour tableau)
const TeamMemberRow = ({ member, onEvaluate, onFeedback, onViewProfile, level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = member.children && member.children.length > 0;
  
  const getInitials = () => {
    return `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.toUpperCase();
  };
  
  return (
    <>
      <div 
        className={`flex items-center gap-4 p-3 hover:bg-slate-50 border-b border-slate-100 transition-colors`}
        style={{ paddingLeft: `${16 + level * 24}px` }}
      >
        {/* Expand/Collapse pour hiérarchie */}
        <div className="w-6">
          {hasChildren && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-slate-200 rounded"
            >
              {isExpanded ? 
                <ChevronDown className="w-4 h-4 text-slate-400" /> : 
                <ChevronRight className="w-4 h-4 text-slate-400" />
              }
            </button>
          )}
        </div>
        
        {/* Avatar */}
        <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
          <AvatarImage src={member.photoUrl} />
          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-700 text-white text-sm">
            {getInitials()}
          </AvatarFallback>
        </Avatar>
        
        {/* Info collaborateur */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900 truncate">
              {member.firstName} {member.lastName}
            </span>
            {member.hasSubordinates && (
              <Badge variant="outline" className="text-xs">
                <Users className="w-3 h-3 mr-1" />
                {member.subordinatesCount}
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500 truncate">{member.jobTitle}</p>
        </div>
        
        {/* Performance */}
        <div className="w-20 text-center">
          {member.performanceScore !== null && member.performanceScore !== undefined ? (
            <div className="flex flex-col items-center">
              <span className={`text-sm font-semibold ${
                member.performanceScore >= 70 ? 'text-green-600' :
                member.performanceScore >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {member.performanceScore}%
              </span>
              <Progress 
                value={member.performanceScore} 
                className="h-1.5 w-16 mt-1" 
              />
            </div>
          ) : (
            <span className="text-xs text-slate-400">Non évalué</span>
          )}
        </div>
        
        {/* Maîtrise - Score basé sur les compétences évaluées */}
        <div className="w-20 text-center" title="Maîtrise des compétences déjà évaluées">
          {member.skillsScore !== null && member.skillsScore !== undefined ? (
            <span className={`text-sm font-semibold ${
              member.skillsScore >= 70 ? 'text-green-600' :
              member.skillsScore >= 50 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {member.skillsScore}%
            </span>
          ) : (
            <span className="text-xs text-slate-400">-</span>
          )}
        </div>
        
        {/* Statut */}
        <div className="w-24">
          <StatusBadge status={member.status} />
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-8 w-8 p-0"
            onClick={() => onViewProfile(member)}
            title="Voir profil"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-8 w-8 p-0"
            onClick={() => onEvaluate(member)}
            title="Évaluer"
          >
            <Star className="w-4 h-4" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-8 w-8 p-0"
            onClick={() => onFeedback(member)}
            title="Feedback"
          >
            <MessageSquare className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Sous-membres (hiérarchie) */}
      {isExpanded && hasChildren && (
        <div className="bg-slate-50/50">
          {member.children.map((child) => (
            <TeamMemberRow 
              key={child.id}
              member={child}
              onEvaluate={onEvaluate}
              onFeedback={onFeedback}
              onViewProfile={onViewProfile}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </>
  );
};

// Radar Chart simplifié pour compétences équipe
const TeamRadarChart = ({ skills }) => {
  if (!skills || skills.length === 0) return null;
  
  const maxLevel = 5;
  const centerX = 100;
  const centerY = 100;
  const radius = 65;
  
  const angleStep = (2 * Math.PI) / Math.min(skills.length, 6);
  const displaySkills = skills.slice(0, 6);
  
  const points = displaySkills.map((skill, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const r = (skill.avgLevel / maxLevel) * radius;
    return {
      x: centerX + r * Math.cos(angle),
      y: centerY + r * Math.sin(angle)
    };
  });
  
  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');
  
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1];
  
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[200px] mx-auto">
      {gridLevels.map((level, i) => (
        <polygon
          key={i}
          points={displaySkills.map((_, j) => {
            const angle = j * angleStep - Math.PI / 2;
            const r = level * radius;
            return `${centerX + r * Math.cos(angle)},${centerY + r * Math.sin(angle)}`;
          }).join(' ')}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="1"
        />
      ))}
      
      {displaySkills.map((_, i) => {
        const angle = i * angleStep - Math.PI / 2;
        return (
          <line
            key={i}
            x1={centerX}
            y1={centerY}
            x2={centerX + radius * Math.cos(angle)}
            y2={centerY + radius * Math.sin(angle)}
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        );
      })}
      
      <polygon
        points={polygonPoints}
        fill="rgba(37, 99, 235, 0.2)"
        stroke="#2563eb"
        strokeWidth="2"
      />
      
      {points.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="4"
          fill={displaySkills[i].avgLevel >= 3 ? '#22c55e' : displaySkills[i].avgLevel >= 2 ? '#f59e0b' : '#ef4444'}
          stroke="white"
          strokeWidth="2"
        />
      ))}
      
      {displaySkills.map((skill, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const labelRadius = radius + 22;
        const x = centerX + labelRadius * Math.cos(angle);
        const y = centerY + labelRadius * Math.sin(angle);
        return (
          <text
            key={i}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-[8px] fill-slate-600"
          >
            {skill.name.length > 10 ? skill.name.substring(0, 10) + '...' : skill.name}
          </text>
        );
      })}
    </svg>
  );
};

// Distribution Performance (barre horizontale)
const PerformanceDistribution = ({ distribution }) => {
  const total = (distribution?.excellence || 0) + (distribution?.progression || 0) + (distribution?.risk || 0);
  if (total === 0) return null;
  
  const excellencePercent = Math.round((distribution.excellence / total) * 100);
  const progressionPercent = Math.round((distribution.progression / total) * 100);
  const riskPercent = Math.round((distribution.risk / total) * 100);
  
  return (
    <div className="space-y-3">
      <div className="flex h-4 rounded-full overflow-hidden bg-slate-100">
        {excellencePercent > 0 && (
          <div 
            className="bg-green-500 transition-all" 
            style={{ width: `${excellencePercent}%` }}
            title={`Excellence: ${distribution.excellence}`}
          />
        )}
        {progressionPercent > 0 && (
          <div 
            className="bg-amber-400 transition-all" 
            style={{ width: `${progressionPercent}%` }}
            title={`Progression: ${distribution.progression}`}
          />
        )}
        {riskPercent > 0 && (
          <div 
            className="bg-red-500 transition-all" 
            style={{ width: `${riskPercent}%` }}
            title={`Risque: ${distribution.risk}`}
          />
        )}
      </div>
      
      <div className="flex justify-between text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
          <span className="text-slate-600">Excellence ({distribution.excellence})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="text-slate-600">Progression ({distribution.progression})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          <span className="text-slate-600">Risque ({distribution.risk})</span>
        </div>
      </div>
    </div>
  );
};

// ==================== PAGE PRINCIPALE ====================

export default function ManagerDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPro, isFree, loading: planLoading } = usePlan();
  
  // États
  const [loading, setLoading] = useState(true);
  const [showHierarchy, setShowHierarchy] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [teamSkills, setTeamSkills] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  
  // États pour dialogs
  const [feedbackDialog, setFeedbackDialog] = useState({ open: false, employee: null });
  const [feedbackContent, setFeedbackContent] = useState('');
  const [feedbackType, setFeedbackType] = useState('feedback');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  
  // États pour Insights IA
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Chargement des données
  const fetchData = useCallback(async () => {
    if (!user?.id || !user?.companyId) return;
    
    const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
    
    setLoading(true);
    try {
      const [teamRes, statsRes, skillsRes, aiRes, activityRes, alertsRes, pendingEvalsRes, allEvalsRes, toValidateRes] = await Promise.all([
        // For admins, get all employees. For managers, get their team.
        isAdmin 
          ? api.get('/employees?limit=50').catch(() => ({ data: { employees: [] } }))
          : api.get(`/manager/team?include_hierarchy=${showHierarchy}`).catch(() => ({ data: { members: [] } })),
        api.get('/manager/team/stats').catch(() => ({ data: null })),
        api.get('/manager/team/skills').catch(() => ({ data: { skills: [] } })),
        api.get('/manager/ai/recommendations').catch(() => ({ data: { recommendations: [] } })),
        api.get('/manager/activity?limit=5').catch(() => ({ data: { activities: [] } })),
        api.get('/manager/alerts').catch(() => ({ data: { alerts: [] } })),
        api.get('/performance-eval/manager/pending-evaluations').catch(() => ({ data: { evaluations: [] } })),
        api.get('/performance-eval/manager/team-evaluations').catch(() => ({ data: { evaluations: [] } })),
        // Get evaluations to validate (for Admin/RH)
        isAdmin ? api.get('/performance-eval/to-validate').catch(() => ({ data: { evaluations: [] } })) : Promise.resolve({ data: { evaluations: [] } })
      ]);
      
      // Get all team evaluations to enrich member data
      const allEvals = allEvalsRes.data?.evaluations || [];
      const evalsByEmployee = {};
      for (const ev of allEvals) {
        evalsByEmployee[ev.employeeId] = ev;
      }
      
      // Get members based on user role
      const rawMembers = isAdmin 
        ? (teamRes.data?.employees || []).filter(e => e.status === 'Actif')
        : (teamRes.data?.members || []);
      
      // Enrich team members with performance scores
      const members = rawMembers.map(member => {
        const memberEval = evalsByEmployee[member.id];
        if (memberEval) {
          return {
            ...member,
            performanceScore: memberEval.managerScore || memberEval.selfScore,
            evaluationStatus: memberEval.status,
            hasEvaluation: true
          };
        }
        return member;
      });
      
      setTeamMembers(members);
      
      // Enhance stats with pending evaluations count and team size
      const stats = statsRes.data || {};
      const pendingEvals = pendingEvalsRes.data?.evaluations || [];
      stats.pendingEvaluationsCount = pendingEvals.length;
      stats.pendingEvaluations = pendingEvals;
      
      // Add evaluations to validate (for Admin/RH)
      const toValidateEvals = toValidateRes.data?.evaluations || [];
      stats.toValidateCount = toValidateEvals.length;
      stats.toValidateEvaluations = toValidateEvals;
      
      if (isAdmin) {
        stats.teamSize = members.length;
      }
      setTeamStats(stats);
      
      setTeamSkills(skillsRes.data?.skills || []);
      setAiRecommendations(aiRes.data?.recommendations || []);
      setActivities(activityRes.data?.activities || []);
      setAlerts(alertsRes.data?.alerts || []);
      
    } catch (error) {
      console.error('Error fetching manager data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.companyId, showHierarchy]);
  
  useEffect(() => {
    fetchData();
    loadAiInsights();
  }, [fetchData]);
  
  // Rafraîchir les données
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    await loadAiInsights();
    setRefreshing(false);
    toast.success('Données actualisées');
  };
  
  // Charger les insights IA
  const loadAiInsights = async () => {
    if (!user?.employeeId || !user?.companyId) return;
    
    setLoadingInsights(true);
    try {
      const res = await api.get(`/manager/team/ai-insights?manager_id=${user.employeeId}&company_id=${user.companyId}`);
      setAiInsights(res.data);
    } catch (error) {
      console.error('Error loading AI insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };
  
  // Télécharger le rapport PDF
  const handleDownloadPdf = async () => {
    if (!user?.employeeId || !user?.companyId) return;
    
    setDownloadingPdf(true);
    try {
      const response = await api.get(
        `/manager/team/report-pdf?manager_id=${user.employeeId}&company_id=${user.companyId}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_equipe_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Rapport PDF téléchargé');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingPdf(false);
    }
  };
  
  // Toggle hiérarchie
  const handleToggleHierarchy = async (value) => {
    setShowHierarchy(value);
  };
  
  // Actions
  const handleViewProfile = (member) => {
    // Manager voit le profil de compétences de l'employé
    navigate(`/skills/employee/${member.id}`);
  };
  
  const handleEvaluate = (member) => {
    // Route vers le formulaire d'évaluation campagne (avec bouton "Renvoyer à l'employé")
    navigate(`/performance/evaluate-campaign/${member.id}`);
  };
  
  const handleFeedback = (member) => {
    setFeedbackDialog({ open: true, employee: member });
    setFeedbackContent('');
    setFeedbackType('feedback');
  };
  
  const handleSubmitFeedback = async () => {
    if (!feedbackContent.trim() || !feedbackDialog.employee) return;
    
    setSubmittingFeedback(true);
    try {
      await api.post('/manager/feedback', {
        employeeId: feedbackDialog.employee.id,
        type: feedbackType,
        content: feedbackContent,
        visibility: 'private'
      });
      
      toast.success('Feedback envoyé avec succès');
      setFeedbackDialog({ open: false, employee: null });
      setFeedbackContent('');
      
      // Rafraîchir les activités
      const activityRes = await api.get('/manager/activity?limit=5');
      setActivities(activityRes.data?.activities || []);
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Erreur lors de l\'envoi du feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };
  
  const handleAIAction = (recommendation) => {
    if (recommendation.action) {
      navigate(recommendation.action);
    }
  };
  
  // Génération du message personnalisé
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bonjour';
    if (hour < 18) return 'Bon après-midi';
    return 'Bonsoir';
  };
  
  // Vérification du rôle
  if (user?.role !== 'Manager' && user?.role !== 'Admin' && user?.role !== 'SuperAdmin') {
    return (
      <DashboardLayout title="Mon équipe">
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2 text-center">
            Accès réservé aux Managers
          </h2>
          <p className="text-slate-600 text-center max-w-md mb-6">
            Ce tableau de bord est destiné aux managers d'équipe.
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            Retour au tableau de bord
          </Button>
        </div>
      </DashboardLayout>
    );
  }
  
  if (loading || planLoading) {
    return (
      <DashboardLayout title="Mon équipe">
        <div className="space-y-6 animate-pulse">
          <div className="h-24 bg-slate-200 rounded-xl"></div>
          <div className="h-32 bg-slate-200 rounded-xl"></div>
          <div className="grid grid-cols-3 gap-4">
            <div className="h-28 bg-slate-200 rounded-xl"></div>
            <div className="h-28 bg-slate-200 rounded-xl"></div>
            <div className="h-28 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  const departmentName = teamMembers.length > 0 ? teamMembers[0]?.departmentName : null;
  
  return (
    <DashboardLayout title="Mon équipe">
      <div className="space-y-6 max-w-7xl mx-auto">
        
        {/* 🟦 HEADER - Message personnalisé */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">
                  {getGreeting()} {user?.firstName}, voici l'état de votre équipe
                </h1>
                <div className="flex items-center gap-4 mt-2 text-blue-100">
                  <Badge className="bg-white/20 text-white border-0">
                    <UserCheck className="w-3.5 h-3.5 mr-1" />
                    Manager
                  </Badge>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    Équipe : {departmentName || 'Mon équipe'} ({teamStats?.directReports || 0} collaborateurs directs)
                  </span>
                </div>
              </div>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* 🟦 BLOC IA INSIGHTS */}
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                </div>
                Insights IA - Performance Équipe
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="gap-2"
              >
                {downloadingPdf ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Rapport PDF
              </Button>
            </div>
            {aiInsights?.summary && (
              <CardDescription className="mt-2">
                {aiInsights.summary}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {loadingInsights ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
              </div>
            ) : aiInsights?.insights?.length > 0 ? (
              <div className="space-y-4">
                {/* Insights Grid */}
                <div className="grid md:grid-cols-2 gap-3">
                  {aiInsights.insights.slice(0, 4).map((insight, idx) => {
                    const IconMap = {
                      'trophy': Trophy,
                      'trending-up': TrendingUp,
                      'alert-triangle': AlertTriangle,
                      'brain': Brain,
                      'alert-circle': AlertCircle,
                      'star': Star,
                      'user-x': UserX,
                      'clock': Clock
                    };
                    const IconComponent = IconMap[insight.icon] || Sparkles;
                    const colorMap = {
                      'success': 'bg-green-50 border-green-200 text-green-700',
                      'warning': 'bg-amber-50 border-amber-200 text-amber-700',
                      'info': 'bg-blue-50 border-blue-200 text-blue-700'
                    };
                    const colorClass = colorMap[insight.type] || 'bg-slate-50 border-slate-200 text-slate-700';
                    
                    return (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border ${colorClass}`}
                      >
                        <div className="flex items-start gap-3">
                          <IconComponent className="h-5 w-5 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-sm">{insight.title}</p>
                            <p className="text-xs mt-1 opacity-80">{insight.message}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Recommendations */}
                {aiInsights.recommendations?.length > 0 && (
                  <div className="pt-3 border-t border-purple-100">
                    <p className="text-sm font-medium text-purple-900 mb-2 flex items-center gap-2">
                      <Lightbulb className="h-4 w-4" />
                      Recommandations
                    </p>
                    <ul className="space-y-1">
                      {aiInsights.recommendations.slice(0, 3).map((rec, idx) => (
                        <li key={idx} className="text-xs text-purple-700 flex items-start gap-2">
                          <span className="text-purple-400">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Sparkles className="h-10 w-10 mx-auto text-purple-300 mb-2" />
                <p className="text-sm">Pas d'insights disponibles</p>
                <p className="text-xs text-slate-400">Ajoutez des collaborateurs pour obtenir des analyses</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* 🟦 BLOC 2 - KPIs ÉQUIPE */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPICard
            title="Collaborateurs"
            value={teamStats?.totalMembers || 0}
            subtitle={`${teamStats?.directReports || 0} directs`}
            icon={Users}
            color="blue"
          />
          <KPICard
            title="Score Performance"
            value={`${teamStats?.avgPerformanceScore || 0}%`}
            icon={Target}
            color="green"
          />
          <KPICard
            title="Maîtrise moyenne"
            value={`${teamStats?.avgSkillsScore || 0}%`}
            subtitle="Compétences évaluées"
            icon={Brain}
            color="purple"
          />
          <KPICard
            title="À risque"
            value={teamStats?.atRiskCount || 0}
            icon={AlertTriangle}
            color={teamStats?.atRiskCount > 0 ? "red" : "green"}
          />
          <KPICard
            title="Évaluations en attente"
            value={teamStats?.pendingEvaluationsCount || 0}
            icon={Clock}
            color={teamStats?.pendingEvaluationsCount > 0 ? "amber" : "green"}
            onClick={() => navigate('/manager/evaluations')}
          />
          {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
            <KPICard
              title="À valider (RH)"
              value={teamStats?.toValidateCount || 0}
              icon={FileText}
              color={teamStats?.toValidateCount > 0 ? "purple" : "green"}
              onClick={() => navigate('/evaluation-campaigns')}
            />
          )}
          <KPICard
            title="Plans upskilling"
            value={teamStats?.activeUpskillingPlans || 0}
            icon={TrendingUp}
            color="blue"
          />
        </div>
        
        {/* 🟦 BLOC 3 - Actions rapides */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Actions rapides</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Alert for pending evaluations */}
            {teamStats?.pendingEvaluationsCount > 0 && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-amber-900">
                      {teamStats.pendingEvaluationsCount} évaluation{teamStats.pendingEvaluationsCount > 1 ? 's' : ''} en attente
                    </h4>
                    <p className="text-sm text-amber-700 mt-1">
                      {teamStats.pendingEvaluations?.slice(0, 2).map(e => 
                        `${e.employee?.firstName} ${e.employee?.lastName}`
                      ).join(', ')}{teamStats.pendingEvaluationsCount > 2 ? '...' : ''} {teamStats.pendingEvaluationsCount === 1 ? 'a' : 'ont'} soumis leur auto-évaluation.
                    </p>
                    <Button 
                      size="sm" 
                      className="mt-2 bg-amber-600 hover:bg-amber-700"
                      onClick={() => navigate('/manager/evaluations')}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Évaluer maintenant
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Alert for evaluations to validate (RH/Admin only) */}
            {teamStats?.toValidateCount > 0 && (
              <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-purple-900">
                      {teamStats.toValidateCount} évaluation{teamStats.toValidateCount > 1 ? 's' : ''} à valider
                    </h4>
                    <p className="text-sm text-purple-700 mt-1">
                      {teamStats.toValidateEvaluations?.slice(0, 3).map(e => 
                        `${e.employee?.firstName || ''} ${e.employee?.lastName || ''}`
                      ).join(', ')}{teamStats.toValidateCount > 3 ? '...' : ''} - Les évaluations managers sont terminées et attendent votre validation.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {teamStats.toValidateEvaluations?.slice(0, 3).map(e => (
                        <Button 
                          key={e.id}
                          size="sm" 
                          className="bg-purple-600 hover:bg-purple-700"
                          onClick={() => navigate(`/rh-validation/${e.id}`)}
                        >
                          <User className="w-4 h-4 mr-2" />
                          {e.employee?.firstName} {e.employee?.lastName?.[0]}.
                        </Button>
                      ))}
                      {teamStats.toValidateCount > 3 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-purple-300 text-purple-700"
                          onClick={() => navigate('/evaluation-campaigns')}
                        >
                          Voir tout ({teamStats.toValidateCount})
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setShowEvalDialog(true)}>
                <Star className="w-4 h-4 mr-2" />
                Lancer une évaluation
              </Button>
              <Button variant="outline" onClick={() => navigate('/manager/evaluations')}>
                <Calendar className="w-4 h-4 mr-2" />
                Mes évaluations
                {teamStats?.pendingEvaluationsCount > 0 && (
                  <Badge className="ml-2 bg-amber-500">{teamStats.pendingEvaluationsCount}</Badge>
                )}
              </Button>
              <Button variant="outline" onClick={() => navigate('/objectives')}>
                <Target className="w-4 h-4 mr-2" />
                Objectifs équipe
              </Button>
              <Button variant="outline" onClick={() => setShowFeedbackDialog(true)}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Donner un feedback
              </Button>
              <Button variant="outline" onClick={() => navigate('/orgchart')}>
                <Network className="w-4 h-4 mr-2" />
                Organigramme
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* 🟦 BLOC 4 - Vue Équipe (Table) */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Mon équipe
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    id="hierarchy-toggle"
                    checked={showHierarchy}
                    onCheckedChange={handleToggleHierarchy}
                  />
                  <Label htmlFor="hierarchy-toggle" className="text-sm text-slate-600 cursor-pointer">
                    <Network className="w-4 h-4 inline mr-1" />
                    Afficher la hiérarchie complète
                  </Label>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {teamMembers.length > 0 ? (
              <div className="overflow-x-auto">
                {/* Header du tableau */}
                <div className="flex items-center gap-4 p-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase">
                  <div className="w-6"></div>
                  <div className="w-10"></div>
                  <div className="flex-1">Collaborateur</div>
                  <div className="w-20 text-center">Performance</div>
                  <div className="w-20 text-center" title="Score basé sur les compétences évaluées">Maîtrise</div>
                  <div className="w-24">Statut</div>
                  <div className="w-28">Actions</div>
                </div>
                
                {/* Corps du tableau */}
                <div>
                  {teamMembers.map((member) => (
                    <TeamMemberRow
                      key={member.id}
                      member={member}
                      onEvaluate={handleEvaluate}
                      onFeedback={handleFeedback}
                      onViewProfile={handleViewProfile}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Aucun collaborateur dans votre équipe</p>
                <p className="text-sm mt-1">Les employés vous seront assignés par l'Admin RH</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* 🟦 BLOC 5 - Graphiques Performance & Compétences */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Gauge className="w-5 h-5 text-blue-600" />
                Répartition Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {teamStats?.distribution ? (
                <PerformanceDistribution distribution={teamStats.distribution} />
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p className="text-sm">Pas assez de données pour afficher la répartition</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                Compétences Équipe
              </CardTitle>
            </CardHeader>
            <CardContent>
              {teamSkills.length > 0 ? (
                <div className="flex flex-col items-center">
                  <TeamRadarChart skills={teamSkills} />
                  <div className="mt-4 w-full">
                    <p className="text-xs text-slate-500 mb-2">Top écarts à combler :</p>
                    {teamSkills.filter(s => s.gap > 0).slice(0, 3).map((skill, i) => (
                      <div key={i} className="flex justify-between items-center text-sm py-1">
                        <span className="text-slate-600">{skill.name}</span>
                        <Badge variant="outline" className={skill.status === 'Critique' ? 'border-red-200 text-red-600' : 'border-amber-200 text-amber-600'}>
                          -{skill.gap}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Brain className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Aucune compétence évaluée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* 🟦 BLOC 6 - Historique & Alertes */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-600" />
                Mes dernières actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.map((activity, i) => (
                    <div key={activity.id || i} className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        {activity.type === 'feedback' ? (
                          <MessageSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-700">{activity.action}</p>
                        <p className="text-xs text-slate-400">
                          {activity.date && new Date(activity.date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Aucune action récente</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                Alertes
                {alerts.length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">{alerts.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.slice(0, 5).map((alert) => (
                    <div 
                      key={alert.id} 
                      className={`p-3 rounded-lg border-l-4 ${
                        alert.type === 'danger' ? 'bg-red-50 border-l-red-500' :
                        alert.type === 'warning' ? 'bg-amber-50 border-l-amber-500' :
                        'bg-blue-50 border-l-blue-500'
                      }`}
                    >
                      <p className="text-sm font-medium text-slate-900">{alert.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{alert.message}</p>
                      {alert.action && (
                        <Button 
                          size="sm" 
                          variant="link" 
                          className="p-0 h-auto mt-1 text-xs"
                          onClick={() => navigate(alert.action)}
                        >
                          {alert.actionLabel || 'Voir'}
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-green-400" />
                  <p className="text-sm">Aucune alerte en cours</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
      </div>
      
      {/* Dialog Choisir un collaborateur pour évaluation */}
      <Dialog open={showEvalDialog} onOpenChange={setShowEvalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-blue-600" />
              Lancer une évaluation
            </DialogTitle>
            <DialogDescription>
              Choisissez un collaborateur à évaluer
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-80 overflow-y-auto">
            {teamMembers.length > 0 ? (
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div 
                    key={member.id}
                    onClick={() => {
                      navigate(`/performance/evaluate-campaign/${member.id}`);
                      setShowEvalDialog(false);
                    }}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={member.photoUrl} />
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {member.firstName?.[0]}{member.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{member.firstName} {member.lastName}</p>
                      <p className="text-sm text-slate-500">{member.jobTitle}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Aucun collaborateur dans votre équipe</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEvalDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Choisir un collaborateur pour feedback */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Donner un feedback
            </DialogTitle>
            <DialogDescription>
              Choisissez un collaborateur
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 max-h-80 overflow-y-auto">
            {teamMembers.length > 0 ? (
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div 
                    key={member.id}
                    onClick={() => {
                      handleFeedback(member);
                      setShowFeedbackDialog(false);
                    }}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={member.photoUrl} />
                      <AvatarFallback className="bg-green-100 text-green-700">
                        {member.firstName?.[0]}{member.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{member.firstName} {member.lastName}</p>
                      <p className="text-sm text-slate-500">{member.jobTitle}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Aucun collaborateur dans votre équipe</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Feedback - pour saisir le contenu */}
      <Dialog open={feedbackDialog.open} onOpenChange={(open) => setFeedbackDialog({ ...feedbackDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Donner un feedback
            </DialogTitle>
            <DialogDescription>
              {feedbackDialog.employee && (
                <span>
                  Feedback pour <strong>{feedbackDialog.employee.firstName} {feedbackDialog.employee.lastName}</strong>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant={feedbackType === 'feedback' ? 'default' : 'outline'}
                onClick={() => setFeedbackType('feedback')}
              >
                Feedback
              </Button>
              <Button 
                size="sm" 
                variant={feedbackType === 'recognition' ? 'default' : 'outline'}
                onClick={() => setFeedbackType('recognition')}
              >
                Reconnaissance
              </Button>
              <Button 
                size="sm" 
                variant={feedbackType === 'improvement' ? 'default' : 'outline'}
                onClick={() => setFeedbackType('improvement')}
              >
                Amélioration
              </Button>
            </div>
            
            <Textarea
              placeholder="Écrivez votre feedback ici..."
              value={feedbackContent}
              onChange={(e) => setFeedbackContent(e.target.value)}
              rows={4}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackDialog({ open: false, employee: null })}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmitFeedback} 
              disabled={!feedbackContent.trim() || submittingFeedback}
            >
              {submittingFeedback ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </DashboardLayout>
  );
}
